// src/services/aiService.js
import api from './api';

/**
 * Send a message to the AI chat endpoint
 * @param {string} message - The user's message
 * @param {string} context - Optional context (e.g., current page, user role)
 * @returns {Promise<{message: string}>}
 */
export async function sendAIMessage(message, context = null) {
    try {
        const response = await api.post('/chat/', {
            message,
            context,
        });
        return response.data;
    } catch (error) {
        console.error('AI Service Error:', error);
        throw new Error(
            error.response?.data?.error || 'Failed to communicate with AI service'
        );
    }
}

/**
 * Get AI explanation for a configuration
 * @param {string} configType - Type of configuration (site, probe, tunnel, etc.)
 * @param {object} configData - The configuration data
 * @returns {Promise<{explanation: string}>}
 */
export async function explainConfiguration(configType, configData) {
    try {
        const response = await api.post('/explain-config/', {
            config_type: configType,
            config_data: configData,
        });
        return response.data;
    } catch (error) {
        console.error('AI Explain Config Error:', error);
        throw new Error(
            error.response?.data?.error || 'Failed to get configuration explanation'
        );
    }
}

/**
 * Get AI troubleshooting help
 * @param {string} issue - Description of the issue
 * @param {object} systemInfo - Optional system information
 * @returns {Promise<{suggestions: string[]}>}
 */
export async function troubleshootIssue(issue, systemInfo = null) {
    try {
        const response = await api.post('/troubleshoot/', {
            issue,
            system_info: systemInfo,
        });
        return response.data;
    } catch (error) {
        console.error('AI Troubleshoot Error:', error);
        throw new Error(
            error.response?.data?.error || 'Failed to get troubleshooting help'
        );
    }
}

/**
 * Get AI-powered tutorial steps
 * @returns {Promise<{steps: Array}>}
 */
export async function getTutorialSteps() {
    try {
        const response = await api.get('/tutorial/');
        return response.data;
    } catch (error) {
        console.error('AI Tutorial Error:', error);
        throw new Error(
            error.response?.data?.error || 'Failed to get tutorial steps'
        );
    }
}
